import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent, useReducer } from "react";
import { STRINGS } from "../../../constants/strings";
import { queryAgentStreamEvents, fetchThreadHistory, cancelAgentRun } from "../api/chatApi";
import { getEmailContext, getEmailAttachments, injectDraftEmail, getSessionId } from "../../../lib/outlook/emailData";
import { TokenStreamParser, generateToolConfirmationA2UI } from "../utils/messageParser";
import type { Message, AgentStep, A2uiMessage } from "../../../types";
import { type Content, type Part, createPartFromBase64 } from "@google/genai";

type ChatState = {
  messages: Message[];
  isLoading: boolean;
  isInitializing: boolean;
};

type ChatAction =
  | { type: 'SET_INITIALIZING'; value: boolean }
  | { type: 'INIT_HISTORY'; messages: Message[] }
  | { type: 'USER_MESSAGE'; message: Message }
  | { type: 'START_ASSISTANT_MESSAGE'; id: string }
  | { type: 'APPEND_TEXT'; id: string; textChunk: string }
  | { type: 'SET_STATUS'; id: string; status: string }
  | { type: 'UPDATE_STEP'; id: string; step: AgentStep }
  | { type: 'ADD_A2UI_MESSAGES'; id: string; messages: A2uiMessage[] }
  | { type: 'SET_BUFFERING'; id: string; isBuffering: boolean }
  | { type: 'SET_ERROR'; id: string; error: string }
  | { type: 'FINISH_MESSAGE'; id: string }
  | { type: 'STOP_LOADING' };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_INITIALIZING':
      return { ...state, isInitializing: action.value };
    case 'INIT_HISTORY':
      return { ...state, messages: action.messages };
    case 'USER_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] };
    case 'START_ASSISTANT_MESSAGE':
      return {
        ...state,
        isLoading: true,
        messages: [
          ...state.messages.map(msg => ({
            ...msg,
            steps: (msg.steps || []).map(s => {
              if (!s.done) {
                let text = s.text.replace('...', '');
                if (text.startsWith('Running action:')) {
                  text = text.replace('Running action:', 'Action completed:');
                } else if (text.startsWith('Transferring to')) {
                  text = text.replace('Transferring to', 'Transferred to');
                }
                return { ...s, done: true, text };
              }
              return s;
            })
          })),
          {
            id: action.id,
            role: "assistant",
            content: "",
            displayContent: "",
            isStreaming: true,
            a2uiMessages: [],
            steps: []
          }
        ]
      };
    case 'APPEND_TEXT':
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.id === action.id) {
            return {
              ...msg,
              content: msg.content + action.textChunk,
              displayContent: (msg.displayContent || "") + action.textChunk,
              status: "" // clear status on new text
            };
          }
          return msg;
        })
      };
    case 'SET_STATUS':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.id ? { ...msg, status: action.status } : msg
        )
      };
    case 'UPDATE_STEP': {
      let stepUpdated = false;

      const nextMessages = state.messages.map(msg => {
        const steps = msg.steps || [];
        const existingIdx = steps.findIndex(s => s.id === action.step.id);
        if (existingIdx >= 0) {
          stepUpdated = true;
          const newSteps = [...steps];
          newSteps[existingIdx] = action.step;
          return { ...msg, steps: newSteps };
        }
        return msg;
      });

      if (!stepUpdated) {
        return {
          ...state,
          messages: nextMessages.map(msg =>
            msg.id === action.id
              ? { ...msg, steps: [...(msg.steps || []), action.step] }
              : msg
          )
        };
      }

      return { ...state, messages: nextMessages };
    }
    case 'ADD_A2UI_MESSAGES':
      return {
        ...state,
        messages: state.messages.map(msg => {
          if (msg.id === action.id) {
            let currentMessages = msg.a2uiMessages ? [...msg.a2uiMessages] : [];

            // For updateComponents, automatically create surface if not exist
            action.messages.forEach(newMsg => {
              if ('updateComponents' in newMsg) {
                const surfaceId = newMsg.updateComponents.surfaceId;
                const hasCreateSurface = currentMessages.some(
                  (m) => 'createSurface' in m && m.createSurface?.surfaceId === surfaceId
                );
                if (!hasCreateSurface && surfaceId) {
                  currentMessages.push({
                    createSurface: {
                      surfaceId,
                      catalogId: "standard",
                    },
                  });
                }
              }
              currentMessages.push(newMsg);
            });

            return { ...msg, a2uiMessages: currentMessages };
          }
          return msg;
        })
      };
    case 'SET_BUFFERING':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.id ? { ...msg, isBufferingA2ui: action.isBuffering } : msg
        )
      };
    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
        messages: state.messages.map(msg =>
          msg.id === action.id ? {
            ...msg,
            content: "Sorry, I encountered an error: " + action.error,
            displayContent: "Sorry, I encountered an error: " + action.error,
            isStreaming: false
          } : msg
        )
      };
    case 'FINISH_MESSAGE':
      return {
        ...state,
        isLoading: false,
        messages: state.messages.map(msg => {
          if (msg.id === action.id) {
            // clear pending transfer steps if we finished
            const steps = (msg.steps || []).map(s => {
              if ((s.text.startsWith('Transferred to ') || s.text.startsWith('Transferring to ')) && !s.done) {
                return { ...s, done: true, text: s.text.replace('Transferring to ', 'Transferred to ').replace('...', '') };
              }
              return s;
            });
            return { ...msg, isStreaming: false, status: "", steps };
          }
          return msg;
        })
      };
    case 'STOP_LOADING':
      return { ...state, isLoading: false };
    default:
      return state;
  }
}

export const useChat = () => {
  const [state, dispatch] = useReducer(chatReducer, {
    messages: [
      {
        id: "1",
        role: "assistant",
        content: STRINGS.CHAT.DEFAULT_GREETING,
        displayContent: STRINGS.CHAT.DEFAULT_GREETING,
      }
    ],
    isLoading: false,
    isInitializing: true,
  });

  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initChat = async () => {
      try {
        const sessionId = getSessionId();
        if (!sessionId) {
          dispatch({ type: 'SET_INITIALIZING', value: false });
          return;
        }

        const thread = await fetchThreadHistory(sessionId);
        if (thread && thread.adkEvents && Array.isArray(thread.adkEvents)) {
          const events = thread.adkEvents;
          if (events.length > 0) {
            const historyMessages: Message[] = [];

            for (const event of events) {
              if (event.content?.role === "developer") continue;

              const hasParts = event.content && event.content.parts && Array.isArray(event.content.parts);
              const hasToolConfirmation = event.actions?.requestedToolConfirmations && Object.keys(event.actions.requestedToolConfirmations).length > 0;

              if (hasParts || hasToolConfirmation) {
                const text = hasParts && event.content?.parts ? event.content.parts.map((p: { text?: string }) => p.text || "").join("") : "";
                if (!text.trim() && !hasToolConfirmation) continue;

                const isUserRole = event.content?.role === "user" && !hasToolConfirmation;
                const role = isUserRole ? "user" : "assistant";
                let displayContent = text;
                let a2uiMessages: A2uiMessage[] = [];
                let isBufferingA2ui = false;

                if (role === "assistant" && text) {
                  let outText = "";
                  const parser = new TokenStreamParser({
                    onTextChunk: (chunk) => { outText += chunk; },
                    onA2UIMessages: (msgs) => { a2uiMessages.push(...msgs); },
                    onBufferingChange: (isBuffering) => { isBufferingA2ui = isBuffering; }
                  });

                  parser.processChunk(text);
                  parser.flush();

                  displayContent = outText;
                }

                if (hasToolConfirmation) {
                  const confirmMsgs = generateToolConfirmationA2UI(event, events);
                  a2uiMessages.push(...confirmMsgs);
                }

                historyMessages.push({
                  id: event.id || Date.now().toString() + Math.random().toString(),
                  role,
                  content: text,
                  displayContent: displayContent.trim(),
                  ...(a2uiMessages.length > 0 ? { a2uiMessages } : {}),
                  ...(isBufferingA2ui ? { isBufferingA2ui } : {})
                });
              }
            }

            if (historyMessages.length > 0) {
              dispatch({ type: 'INIT_HISTORY', messages: historyMessages });
            }
          }
        }
      } catch (err) {
        console.error("Error initializing chat:", err);
      } finally {
        dispatch({ type: 'SET_INITIALIZING', value: false });
      }
    };

    initChat();
  }, []);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state.messages, state.isLoading, state.isInitializing]);

  const handleSend = async (actionPayload?: Content) => {
    if (!actionPayload && !inputValue.trim() && attachedFiles.length === 0) return;
    if (state.isLoading) return;

    const parts: Part[] = actionPayload?.parts ? [...actionPayload.parts] : [{ text: inputValue }];
    const filesToProcess = [...attachedFiles];

    if (!actionPayload) {
      const newMessage: Message = {
        id: Date.now().toString(),
        role: "user",
        content: inputValue,
        displayContent: inputValue,
        files: [...attachedFiles]
      };

      dispatch({ type: 'USER_MESSAGE', message: newMessage });
      setInputValue("");
      setAttachedFiles([]);
    }

    if (textareaRef.current) {
      textareaRef.current.style.height = "32px";
    }

    const assistantMessageId = Date.now().toString() + "-ast";
    dispatch({ type: 'START_ASSISTANT_MESSAGE', id: assistantMessageId });

    try {
      const emailContext = await getEmailContext();
      for (const file of filesToProcess) {
        try {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          parts.push(createPartFromBase64(base64, file.type || "application/octet-stream"));
        } catch (e) {
          console.error("Error reading attached file:", e);
        }
      }

      try {
        const emailAttachments = await getEmailAttachments();
        console.log({ emailAttachments });
        for (const attachment of emailAttachments) {
          parts.push(createPartFromBase64(attachment.base64Data, attachment.contentType || "application/octet-stream"));
        }
      } catch (e) {
        console.error("Error getting email attachments:", e);
      }

      const sessionId = getSessionId();
      let lastInjectedDraftStr = "";

      const parser = new TokenStreamParser({
        onTextChunk: (chunk) => {
          dispatch({ type: 'APPEND_TEXT', id: assistantMessageId, textChunk: chunk });
        },
        onA2UIMessages: (messages) => {
          dispatch({ type: 'ADD_A2UI_MESSAGES', id: assistantMessageId, messages });
        },
        onDraftEmail: (draftObj) => {
          const draftStr = JSON.stringify(draftObj);
          if (draftStr !== lastInjectedDraftStr) {
            lastInjectedDraftStr = draftStr;
            injectDraftEmail(draftObj).catch(err => console.error("Error auto-injecting draft:", err));
          }
        },
        onBufferingChange: (isBuffering) => {
          dispatch({ type: 'SET_BUFFERING', id: assistantMessageId, isBuffering });
        }
      });

      await queryAgentStreamEvents(parts, emailContext, sessionId, (action) => {
        switch (action.type) {
          case 'text_chunk':
            parser.processChunk(action.text);
            break;
          case 'status':
            dispatch({ type: 'SET_STATUS', id: assistantMessageId, status: action.status });
            break;
          case 'step_update':
            dispatch({ type: 'UPDATE_STEP', id: assistantMessageId, step: action.step });
            break;
          case 'raw_a2ui':
            dispatch({ type: 'ADD_A2UI_MESSAGES', id: assistantMessageId, messages: [action.message] });
            break;
          case 'error':
            dispatch({ type: 'SET_ERROR', id: assistantMessageId, error: action.error });
            break;
          case 'done':
            parser.flush();
            dispatch({ type: 'FINISH_MESSAGE', id: assistantMessageId });
            break;
        }
      });
    } catch (error) {
      console.error("Error querying agent:", error);
      dispatch({ type: 'SET_ERROR', id: assistantMessageId, error: "Failed to communicate with agent." });
    } finally {
      dispatch({ type: 'STOP_LOADING' });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "32px";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setAttachedFiles((prev) => [...prev, ...filesArray]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (indexToRemove: number) => {
    setAttachedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleCancelRun = async () => {
    const sessionId = getSessionId();
    if (!sessionId) return;
    try {
      await cancelAgentRun(sessionId);
    } catch (err) {
      console.error("Error cancelling run:", err);
    }
  };

  return {
    messages: state.messages,
    inputValue,
    attachedFiles,
    isLoading: state.isLoading,
    isInitializing: state.isInitializing,
    fileInputRef,
    textareaRef,
    endOfMessagesRef,
    handleSend,
    handleCancelRun,
    handleKeyDown,
    handleInputChange,
    handleFileChange,
    removeFile
  };
};